import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import deviewLogo from '@/assets/images/deview-logo.svg';
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Drawer,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SearchIcon from '@mui/icons-material/Search';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import HistoryIcon from '@mui/icons-material/History';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES, ROUTE_CONFIG } from '@/constants/route-config';
import { extractLocaleFromPath, getLocalizedPath } from '@/utils/locale';
import { useAuth0 } from '@auth0/auth0-react';
import useAppStore from '@/stores/appStore';
import { RegisterDialog } from '@/components/modules/auth/RegisterDialog';
import { useLogin, useLogout } from '@/api/axios/auth';
import { useUserProfile } from '@/api/axios/user';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import {
  SESSION_EXPIRED_EVENT,
  pingSession,
  type SessionExpiredDetail,
  type SessionExpiredReason,
} from '@/api/axios/axiosClient';
import { useSessionExpiryStore } from '@/stores/sessionExpiryStore';
import { useSessionExpiryReminder } from '@/hooks/useSessionExpiryReminder';
import { requiresReauthentication } from '@/utils/authErrors';
import { GLOBAL_TOAST_EVENT, type GlobalToastDetail } from '@/utils/globalToast';
import { SessionExpiredDialog } from '@/components/modules/auth/SessionExpiredDialog';
import { SessionExpiringDialog } from '@/components/modules/auth/SessionExpiringDialog';
import { useColorMode } from '@/theme/ColorModeContext';
import { darkTokens } from '@/theme/tokens';
import { AboutNavigation } from '@/components/Layout/AboutNavigation';
import { Copyright } from '@/components/Layout/Copyright';
import {
  UserProfilePopover,
  type ProfilePopoverPlacement,
} from '@/components/Layout/UserProfilePopover';

const drawerWidth = 224;

/** Cap on how long the backend /logout/ call may delay the expiry dialog. */
const BACKEND_LOGOUT_TIMEOUT_MS = 3000;

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { mode, toggleColorMode } = useColorMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithRedirect, isAuthenticated, logout: auth0Logout, isLoading, user, getAccessTokenSilently } = useAuth0();
  
  const loginOnceRef = useRef(false);
  const [loginTokenError, setLoginTokenError] = useState(false);
  const [accountCacheVersion, setAccountCacheVersion] = useState(0);
  const wasAuthenticatedRef = useRef(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('error');
  const { setUser, setLoggingOut, clearLoggingOutFlag, registrationRequired, backendUserId } = useAppStore();
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [expiredReason, setExpiredReason] = useState<SessionExpiredReason>('unknown');
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState<null | HTMLElement>(null);
  const [avatarMenuPlacement, setAvatarMenuPlacement] =
    useState<ProfilePopoverPlacement>('header');

  // Network connectivity monitoring (initializes global network handling)
  useNetworkStatus();

  // The self-profile query waits for the backend account ID from login.
  // React Query shares this cache with pages that call the same hook.
  const {
    data: userProfile,
    isLoading: userProfileLoading,
    error: userProfileError,
  } = useUserProfile({ enabled: !isLoading && isAuthenticated });
  
  // Backend authentication hooks (only called on button clicks)  
  const backendLogin = useLogin({
    onSuccess: (data) => {
      if (data === null) return;
      setToastMessage(
        t('layout.toast.login-success', { defaultValue: 'Login successful!' }),
      );
      setToastSeverity('success');
      setToastOpen(true);
    },
    onError: (_error) => {
      setToastMessage(
        t('layout.toast.login-sync-failed', {
          defaultValue: 'Login successful, but backend sync failed',
        }),
      );
      setToastSeverity('error');
      setToastOpen(true);
    }
  });
  
  // Use refs to capture latest functions without triggering re-renders
  const backendLoginRef = useRef(backendLogin);
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  
  // Update refs when functions change
  backendLoginRef.current = backendLogin;
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;
  
  const backendLogout = useLogout({
    onSuccess: () => {
      // Backend logout acknowledged
    },
    onError: (_error) => {
      // Backend logout failed
    }
  });

  const clearAccountCache = useCallback(() => {
    const loggingOut = useAppStore.getState().session.isLoggingOut;
    useAppStore.getState().logout();
    // Keep teardown's guard until its existing confirmation/login flow clears it.
    if (loggingOut) useAppStore.getState().setLoggingOut();
    // Public strategy/profile queries can also contain owner-specific fields.
    // Clear everything, including mutation results, rather than maintaining an
    // incomplete list of private query keys. Clear cancels outstanding queries.
    queryClient.clear();
    useSessionExpiryStore.getState().clear();
    // Mounted public pages must drop observer/local state from the old account
    // and fetch a fresh guest response after local logout.
    setAccountCacheVersion((version) => version + 1);
  }, [queryClient]);
  
  
  // Expire the session locally (idle timeout or 401) and surface the dialog.
  // We do a LOCAL-ONLY Auth0 logout (openUrl: false) — this clears the cached
  // session/tokens WITHOUT redirecting to Auth0's /v2/logout endpoint, which was
  // landing users on the Auth0 error page. The user is kept in the SPA and is
  // redirected to Discover only after acknowledging the dialog.
  const expireSession = useCallback(async (options?: { tokenUnavailable?: boolean }) => {
    // Idempotency guard: the reminder's onExpire and a concurrent 401 can both
    // reach here — isLoggingOut is set below and reset on next login (setUser),
    // so a second call during teardown is a no-op.
    if (useAppStore.getState().session.isLoggingOut) return;

    // Prevent RequireAuth / auto-login races while we tear the session down
    setLoggingOut();
    setLoginTokenError(false);
    setToastOpen(false);

    // Drop the live idle countdown so the "expiring soon" reminder can't linger
    useSessionExpiryStore.getState().clear();

    // Notify the backend BEFORE tearing Auth0 down locally, and AWAIT it.
    // auth0Logout() nulls the axios token getter, and the request interceptor
    // then refuses to send a token-less request — so a fire-and-forget mutate()
    // here loses the race and /logout/ never leaves the browser, silently
    // leaving the backend session alive. Bounded so a hung request can't block
    // the expiry dialog.
    if (!options?.tokenUnavailable) {
      try {
        const accessToken = await getAccessTokenSilently();
        await Promise.race([
          backendLogout.mutateAsync({ accessToken }),
          new Promise((resolve) => setTimeout(resolve, BACKEND_LOGOUT_TIMEOUT_MS)),
        ]);
      } catch {
        // Token unavailable/expired, or the backend rejected it — nothing more
        // we can do; proceed with the local logout regardless.
      }
    }

    // Local-only Auth0 logout: no redirect to Auth0, no error page
    try {
      await auth0Logout({ openUrl: false });
    } catch (error) {
      console.error('[Auth] Local logout failed', error);
    }

    // Clear local app/user state
    useAppStore.getState().setBackendUserId(null);
    clearAccountCache();
    useAppStore.getState().logout();

    // Surface the "session expired" dialog
    setSessionExpiredOpen(true);
  }, [
    setLoggingOut,
    getAccessTokenSilently,
    backendLogout,
    auth0Logout,
    clearAccountCache,
  ]);

  // Keep a stable ref so the event listener below doesn't re-subscribe each render
  const expireSessionRef = useRef(expireSession);
  expireSessionRef.current = expireSession;

  // Cold-start login and the Retry button must distinguish an expired Auth0
  // session from a temporary network failure, just like API token acquisition.
  const handleLoginTokenError = useCallback((error: unknown) => {
    if (useAppStore.getState().session.isLoggingOut) return;
    if (requiresReauthentication(error)) {
      setExpiredReason('invalid_token');
      void expireSessionRef.current({ tokenUnavailable: true });
      return;
    }
    setLoginTokenError(true);
    setToastMessage(t('layout.toast.sign-in-incomplete'));
    setToastSeverity('error');
    setToastOpen(true);
  }, [t]);

  // NOTE: The app no longer runs a client-side idle timer. The backend owns
  // session lifetime — it idle-expires a session after ~1h of no API calls, and
  // any authenticated request extends it. Expiry is detected reactively via the
  // 401 (inner code 1001 `session_expired`) that the axios interceptor turns into
  // the SESSION_EXPIRED_EVENT handled below. The flow editor runs its own
  // UI-activity idle timer (useIdleTimeout) since auto-save keeps its session alive.

  // Route detection (also used for full-width layout below).
  const { locale: currentLocale, path: pathWithoutLocale } =
    extractLocaleFromPath(location.pathname);
  const fullWidthRoutes: string[] = [ROUTES.BOT_FLOW_EDITOR, ROUTES.BOT_FLOW_PREVIEW];
  const isFullWidthRoute = fullWidthRoutes.includes(pathWithoutLocale);
  // The flow editor runs its own UI-activity idle timer — auto-save keeps the
  // backend session alive there, so the server-driven reminder would never fire.
  const isFlowEditorRoute = pathWithoutLocale === ROUTES.BOT_FLOW_EDITOR;

  // Proactive "session expiring soon" reminder, driven by the live Session-Expires-In
  // countdown captured from responses (suppressed on the flow editor — see above).
  // onExpire fires when it hits zero so we show the hard-expiry dialog immediately
  // rather than waiting for the next API 401.
  const [staying, setStaying] = useState(false);
  const sessionReminder = useSessionExpiryReminder(
    !isLoading && isAuthenticated && !isFlowEditorRoute,
    () => {
      void expireSessionRef.current();
    },
  );

  // "Stay signed in": a real authenticated call — only API activity extends the
  // backend window. Dismiss after success even if no expiry header is returned.
  // Failed requests leave the reminder available; auth failures use the expiry flow.
  const dismissSessionReminder = sessionReminder.dismiss;
  const handleStaySignedIn = useCallback(async () => {
    setStaying(true);
    try {
      await pingSession();
      dismissSessionReminder();
    } catch {
      // Dead session — expiry flow handles it
    } finally {
      setStaying(false);
    }
  }, [dismissSessionReminder]);

  const handleReminderLogout = useCallback(() => {
    sessionReminder.dismiss();
    void expireSessionRef.current();
  }, [sessionReminder]);

  // Listen for session-expired events from axios interceptor (401 responses).
  // Only trigger logout when auth has settled AND the user is authenticated:
  //  - while Auth0 is still loading (e.g. right after the login redirect) a late
  //    401 from a token-less request must NOT kick the user out.
  //  - unauthenticated users hitting 401s on public pages must not be redirected.
  useEffect(() => {
    const handleSessionExpired = (e: Event) => {
      if (isLoading) {
        console.info('[Auth] Session expired event ignored — auth still loading');
        return;
      }
      if (!isAuthenticated) {
        console.info('[Auth] Session expired event ignored — user is not authenticated');
        return;
      }
      const detail = (e as CustomEvent<SessionExpiredDetail>).detail;
      const reason = detail?.reason ?? 'unknown';
      setExpiredReason(reason);
      console.warn(`[Auth] Session expired event received (${reason}) — expiring session`);
      void expireSessionRef.current({ tokenUnavailable: detail?.tokenUnavailable });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [isAuthenticated, isLoading]);

  // User acknowledged the expiry dialog — clear flags and return to Discover (Home)
  const handleSessionExpiredConfirm = useCallback(() => {
    setSessionExpiredOpen(false);
    clearLoggingOutFlag();
    navigate(getLocalizedPath(ROUTES.DISCOVER));
  }, [clearLoggingOutFlag, navigate]);

  // Listen for global toast events from any component
  useEffect(() => {
    const handleGlobalToast = (e: Event) => {
      const { message, severity } = (e as CustomEvent<GlobalToastDetail>).detail;
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
    };

    window.addEventListener(GLOBAL_TOAST_EVENT, handleGlobalToast);
    return () => window.removeEventListener(GLOBAL_TOAST_EVENT, handleGlobalToast);
  }, []);
  
  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleAvatarMenuOpen = (
    placement: ProfilePopoverPlacement,
    e: React.MouseEvent<HTMLElement>,
  ) => {
    setAvatarMenuPlacement(placement);
    setAvatarMenuAnchor(e.currentTarget);
  };
  const handleAvatarMenuClose = () => setAvatarMenuAnchor(null);

  const pageTitle = useMemo(() => {
    // Root and locale-only URLs redirect after Auth0 initializes. Avoid showing
    // the brand fallback (or guessing Discover) before the landing page is known.
    if (pathWithoutLocale === ROUTES.ROOT && isLoading) return null;
    const titlePath = pathWithoutLocale === ROUTES.ROOT
      ? (isAuthenticated ? ROUTES.DASHBOARD : ROUTES.DISCOVER)
      : pathWithoutLocale;
    const sorted = ROUTE_CONFIG.filter((r) => r.path !== '*').sort(
      (a, b) => b.path.length - a.path.length,
    );
    const match = sorted.find((route) => {
      if (route.path.includes(':')) {
        const routePattern = route.path
          .replace(/:[^/]+/g, '[^/]+')
          .replace(/\//g, '\\/');
        return new RegExp(`^${routePattern}$`).test(titlePath);
      }

      return (
        titlePath === route.path ||
        titlePath.startsWith(route.path + '/')
      );
    });
    return match?.title ?? 'Deview';
  }, [pathWithoutLocale, isLoading, isAuthenticated]);

  // Handle Auth0 state changes
  useEffect(() => {
    if (isLoading) return;
    
    if (!isAuthenticated) {
      loginOnceRef.current = false;
      setLoginTokenError(false);
      useAppStore.getState().setRegistrationRequired(false);
      useAppStore.getState().setBackendUserId(null);
      // Also handle Auth0 becoming unauthenticated outside our logout buttons.
      // Do not clear on an initial guest visit: public queries may be loading.
      if (wasAuthenticatedRef.current || useAppStore.getState().user.isAuthenticated) {
        wasAuthenticatedRef.current = false;
        clearAccountCache();
      }
      // Don't auto-clear isLoggingOut flag here - let user manually click Login
      // The flag will be cleared when they successfully login (setUser resets it)
      return;
    }

    wasAuthenticatedRef.current = true;
    
    if (loginOnceRef.current) return;

    if (user) {
      // Sync Auth0 user to store (setUser also resets isLoggingOut to false)
      setUser({
        id: user.sub || '',
        email: user.email || '',
        name: user.name || '',
        picture: user.picture || '',
      });
      
      // Call backend acknowledgment after Auth0 success (once per login)
      getAccessTokenSilentlyRef.current()
        .then(accessToken => {
          if (useAppStore.getState().session.isLoggingOut) return;
          backendLoginRef.current.mutate({ accessToken });
        })
        .catch(handleLoginTokenError);
      loginOnceRef.current = true;
    }
  }, [isLoading, isAuthenticated, user, setUser, t, clearAccountCache, handleLoginTokenError]);

  // Shared logout handler (used by the header button and the sidebar footer).
  const handleLogout = useCallback(async () => {
    // 1. Set logging-out flag FIRST to prevent RequireAuth re-login race
    setLoggingOut();

    try {
      // 2. Notify backend of logout (fire-and-forget, don't await)
      const accessToken = await getAccessTokenSilently();
      backendLogout.mutate({ accessToken });
    } catch (_error) {
      // Failed to get access token for logout - proceed anyway
    }

    // 3. Auth0 logout redirects externally — page will unload
    useAppStore.getState().setBackendUserId(null);
    clearAccountCache();
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setLoggingOut, getAccessTokenSilently, auth0Logout, clearAccountCache]);

  const profileName =
    userProfile?.name ||
    userProfile?.username ||
    user?.name ||
    user?.nickname ||
    user?.email ||
    t('layout.user', { defaultValue: 'User' });
  const profileEmail = userProfile?.email || user?.email || '';
  const profileImage = userProfile?.image || user?.picture || undefined;

  // Primary nav items
  const allPrimaryMenuItems = [
    { text: t('nav.dashboard', { defaultValue: 'Dashboard' }), icon: <DashboardOutlinedIcon />, path: ROUTES.DASHBOARD, requireAuth: true },
    { text: t('nav.discover', { defaultValue: 'Discover' }), icon: <SearchIcon />, path: ROUTES.DISCOVER },
    { text: t('nav.bot-system', { defaultValue: 'Strategy Management' }), icon: <SmartToyOutlinedIcon />, path: ROUTES.BOT_SYSTEM, requireAuth: true },
    { text: t('nav.alert-history', { defaultValue: 'Alert History' }), icon: <NotificationsNoneIcon />, path: ROUTES.ALERT_HISTORY, requireAuth: true },
    { text: t('nav.network', { defaultValue: 'My network' }), icon: <PeopleOutlineIcon />, path: ROUTES.NETWORK, requireAuth: true },
    { text: t('nav.pricing', { defaultValue: 'Pricing' }), icon: <StarBorderIcon />, path: ROUTES.PRICING },
  ];

  const primaryMenuItems = allPrimaryMenuItems.filter(
    (item) => !item.requireAuth || isAuthenticated,
  );

  // Account section items
  const accountMenuItems = [
    { text: t('nav.channels', { defaultValue: 'Channels' }), icon: <SendOutlinedIcon />, path: ROUTES.CHANNELS },
    { text: t('nav.credits', { defaultValue: 'Credits' }), icon: <CreditCardIcon />, path: ROUTES.CREDIT_MANAGEMENT },
    { text: t('nav.payment-history', { defaultValue: 'Payment history' }), icon: <HistoryIcon />, path: ROUTES.PAYMENT_HISTORY },
  ];

  const isSelected = (path: string) => pathWithoutLocale.startsWith(path);

  // The sidebar stays dark in both modes, so use dark-surface colors throughout.
  const onSidebar = darkTokens.textPrimary;
  const onSidebarBorder = darkTokens.border;
  const sectionLabelSx = {
    px: 2,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '1px',
    textTransform: 'uppercase' as const,
    color: darkTokens.textTertiary,
  };

  // fontSize follows the design: workspace nav rows are 13px, account rows 12.5px.
  const renderNavItem = (
    item: { text: string; icon: React.ReactNode; path: string },
    fontSize = 13,
  ) => {
    const active = isSelected(item.path);
    return (
      <ListItemButton
        key={item.text}
        onClick={() =>
          navigate(getLocalizedPath(item.path, currentLocale ?? undefined))
        }
        sx={{
          flexGrow: 0,
          borderRadius: 1,
          minHeight: 0,
          py: 1,
          px: 1.25,
          color: active ? darkTokens.accent : darkTokens.textSecondary,
          bgcolor: active ? alpha(darkTokens.accent, 0.13) : 'transparent',
          '&:hover': {
            bgcolor: active
              ? alpha(darkTokens.accent, 0.16)
              : 'rgba(255,255,255,0.04)',
          },
        }}
      >
        <ListItemIcon
          sx={{ minWidth: 0, mr: 1.25, color: 'inherit', '& svg': { fontSize: 15 } }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{ fontSize, fontWeight: active ? 600 : 500 }}
        />
      </ListItemButton>
    );
  };

  const drawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        bgcolor: darkTokens.sidebar,
      }}
    >
      {/* Brand header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1.75,
          borderBottom: `1px solid ${darkTokens.border}`,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: darkTokens.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box component="img" src={deviewLogo} alt="" sx={{ width: 28, height: 28, display: 'block' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.3px', color: onSidebar, lineHeight: 1.1 }}>
            {t('layout.brand-name', { defaultValue: 'Deview' })}
          </Typography>
          <Typography noWrap sx={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.6px', textTransform: 'uppercase', color: darkTokens.textTertiary, mt: '2px' }}>
            {t('layout.brand-tagline', { defaultValue: 'Monitoring' })}
          </Typography>
        </Box>
      </Box>

      {/* Workspace section */}
      <Typography sx={{ ...sectionLabelSx, pt: 1.75, pb: 0.75 }}>
        {t('nav.section.workspace', { defaultValue: 'Workspace' })}
      </Typography>
      <List sx={{ flex: 1, px: 1.25, py: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {primaryMenuItems.map((item) => renderNavItem(item))}
      </List>

      <AboutNavigation onNavigate={() => { if (isMobile) setOpen(false); }} />

      {/* Account section — only shown when authenticated */}
      {isAuthenticated && (
        <Box sx={{ borderTop: `1px solid ${darkTokens.border}`, pt: 1.25, pb: 0.5 }}>
          <Typography sx={{ ...sectionLabelSx, pb: 1 }}>
            {t('layout.account', { defaultValue: 'Account' })}
          </Typography>
          <List sx={{ px: 1.25, py: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {accountMenuItems.map((item) => renderNavItem(item, 12.5))}
          </List>
        </Box>
      )}

      {/* User footer */}
      {isAuthenticated && (
        <Box
          sx={{
            borderTop: `1px solid ${darkTokens.border}`,
            px: 1.75,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
          }}
        >
          <IconButton
            onClick={(event) => handleAvatarMenuOpen('sidebar', event)}
            size="small"
            aria-label={t('layout.open-profile-sidebar', {
              defaultValue: 'Open profile menu from sidebar',
            })}
            sx={{ p: 0 }}
          >
            <Avatar
              src={profileImage}
              sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700, bgcolor: darkTokens.accent, color: darkTokens.onAccent }}
            >
              {profileName.charAt(0).toUpperCase() || <AccountCircleIcon />}
            </Avatar>
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: onSidebar }}>
              {profileName}
            </Typography>
            {profileEmail && (
              <Typography noWrap sx={{ fontSize: 11, color: darkTokens.textTertiary }}>
                {profileEmail}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleLogout}
            size="small"
            aria-label={t('layout.logout', { defaultValue: 'Logout' })}
            sx={{ color: darkTokens.textTertiary }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Backdrop open={isLoading} sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 2 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {!isFullWidthRoute && (
        <>
          <AppBar
            position="fixed"
            elevation={0}
            sx={{
              zIndex: theme.zIndex.drawer + 1,
              bgcolor: theme.tokens.headerBg,
              color: theme.tokens.textPrimary,
              borderBottom: `1px solid ${theme.tokens.border}`,
              backgroundImage: 'none',
              width: { md: `calc(100% - ${drawerWidth}px)` },
              ml: { md: `${drawerWidth}px` },
            }}
          >
            <Toolbar variant="dense" sx={{ minHeight: 52, px: 3, gap: 1.5 }}>
              {/* Mobile-only: open the temporary drawer. Desktop sidebar is permanent. */}
              <IconButton
                color="inherit"
                aria-label={t('layout.open-drawer', { defaultValue: 'open drawer' })}
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1, display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>

              {/* Current page title */}
              <Typography variant="h4" noWrap component="div" sx={{ flexGrow: 1 }}>
                {pageTitle}
              </Typography>

              {/* Theme toggle — bordered chip with label */}
              <Box
                onClick={toggleColorMode}
                role="button"
                aria-label={
                  mode === 'dark'
                    ? t('layout.switch-to-light-mode', {
                        defaultValue: 'Switch to light mode',
                      })
                    : t('layout.switch-to-dark-mode', {
                        defaultValue: 'Switch to dark mode',
                      })
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.375,
                  py: 0.625,
                  borderRadius: 0.75,
                  bgcolor: theme.tokens.surface2,
                  border: `1px solid ${theme.tokens.border}`,
                  cursor: 'pointer',
                  fontSize: 11.5,
                  color: theme.tokens.textSecondary,
                  userSelect: 'none',
                  '&:hover': { borderColor: theme.tokens.border2 },
                }}
              >
                {mode === 'dark' ? (
                  <LightModeIcon sx={{ fontSize: 13 }} />
                ) : (
                  <DarkModeIcon sx={{ fontSize: 13 }} />
                )}
                {mode === 'dark'
                  ? t('layout.light-mode', { defaultValue: 'Light mode' })
                  : t('layout.dark-mode', { defaultValue: 'Dark mode' })}
              </Box>

              {/* Login button for unauthenticated users */}
              {!isLoading && !isAuthenticated && (
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      setToastMessage(
                        t('layout.toast.redirecting', {
                          defaultValue: 'Redirecting to login…',
                        }),
                      );
                      setToastSeverity('success');
                      setToastOpen(true);
                      await loginWithRedirect({
                        appState: {
                          returnTo: getLocalizedPath(
                            ROUTES.DASHBOARD,
                            currentLocale ?? undefined,
                          ),
                        },
                        authorizationParams: {
                          redirect_uri: window.location.origin,
                          prompt: 'login',
                        },
                      });
                    } catch (err) {
                      console.error('[Auth] loginWithRedirect error', err);
                      setToastMessage(
                        t('layout.toast.login-failed', {
                          defaultValue: 'Unable to start login. Please try again.',
                        }),
                      );
                      setToastSeverity('error');
                      setToastOpen(true);
                    }
                  }}
                >
                  {t('layout.login', { defaultValue: 'Login' })}
                </Button>
              )}

              {/* Authenticated: avatar opens the shared profile summary. */}
              {isAuthenticated && (
                <IconButton
                  onClick={(event) => handleAvatarMenuOpen('header', event)}
                  size="small"
                  aria-label={t('layout.open-profile-header', {
                    defaultValue: 'Open profile menu',
                  })}
                  sx={{ p: 0 }}
                >
                  <Avatar
                    src={profileImage}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'primary.main',
                      color: theme.tokens.onAccent,
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    {profileName.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              )}
            </Toolbar>
          </AppBar>
          <Box
            component="nav"
            sx={{
              width: { md: drawerWidth },
              flexShrink: { md: 0 },
            }}
          >
            <Drawer
              variant={isMobile ? 'temporary' : 'permanent'}
              open={isMobile ? open : true}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better mobile performance
              }}
              sx={{
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: drawerWidth,
                  bgcolor: theme.tokens.sidebar,
                  borderRight: `1px solid ${onSidebarBorder}`,
                },
              }}
            >
              {drawer}
            </Drawer>
          </Box>
        </>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          // Flex items default to min-width:auto, which would size this to the
          // widest child (e.g. a wide table) and push the page past the
          // viewport. Let it shrink so overflow scrolls inside the child.
          minWidth: 0,
          p: isFullWidthRoute ? 0 : 3,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {!isFullWidthRoute && <Toolbar variant="dense" sx={{ minHeight: 52 }} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isLoading || (isAuthenticated && !backendUserId) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              {backendLogin.isError || loginTokenError ? (
                <Alert severity="error" action={<Button onClick={() => {
                  void getAccessTokenSilently().then(accessToken => {
                    if (useAppStore.getState().session.isLoggingOut) return;
                    setLoginTokenError(false);
                    backendLogin.mutate({ accessToken });
                  }).catch(handleLoginTokenError);
                }}>{t('profile-editor.retry')}</Button>}>{t('layout.sign-in-incomplete')}</Alert>
              ) : !registrationRequired && <CircularProgress aria-label={t('layout.completing-sign-in')} />}
            </Box>
          ) : <React.Fragment key={accountCacheVersion}>{children}</React.Fragment>}
        </Box>
        <Copyright />
      </Box>
      {isAuthenticated && (
        <UserProfilePopover
          anchorEl={avatarMenuAnchor}
          placement={avatarMenuPlacement}
          profile={userProfile}
          profileLoading={userProfileLoading}
          profileError={userProfileError}
          fallbackUser={{
            name: user?.name,
            nickname: user?.nickname,
            email: user?.email,
            picture: user?.picture,
          }}
          mode={mode}
          onClose={handleAvatarMenuClose}
          onToggleTheme={toggleColorMode}
          onLogout={() => {
            handleAvatarMenuClose();
            void handleLogout();
          }}
        />
      )}
      {isAuthenticated && registrationRequired && <RegisterDialog />}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
      <SessionExpiringDialog
        open={sessionReminder.open}
        remainingMs={sessionReminder.remainingMs}
        onStay={handleStaySignedIn}
        onLogout={handleReminderLogout}
        staying={staying}
      />
      <SessionExpiredDialog
        open={sessionExpiredOpen}
        reason={expiredReason}
        onConfirm={handleSessionExpiredConfirm}
      />
    </Box>
  );
};

export default MainLayout;
